import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Pencil, Trash2, Search, Download, Upload, Building2, Users,
  TrendingUp, Calendar, RefreshCw, IndianRupee, FileText, Eye, X, CreditCard, Printer
} from 'lucide-react';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const DEFAULT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function Expenditure() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Filter states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [payingExpense, setPayingExpense] = useState(null);
  const [generatingRecurring, setGeneratingRecurring] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [receiptBlobUrl, setReceiptBlobUrl] = useState('');
  const [receiptLoading, setReceiptLoading] = useState(false);

  // Load receipt with authentication
  const loadReceipt = async (url) => {
    if (!url) return;
    setReceiptUrl(url);
    setReceiptLoading(true);
    setShowReceiptModal(true);
    try {
      // Fetch the receipt with authentication
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      setReceiptBlobUrl(blobUrl);
    } catch (error) {
      toast.error('Failed to load receipt');
      console.error('Receipt load error:', error);
    } finally {
      setReceiptLoading(false);
    }
  };

  // Cleanup blob URL when modal closes
  const closeReceiptModal = () => {
    if (receiptBlobUrl) {
      URL.revokeObjectURL(receiptBlobUrl);
    }
    setReceiptBlobUrl('');
    setReceiptUrl('');
    setShowReceiptModal(false);
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, vendorRes, expRes, recRes, sumRes] = await Promise.all([
        api.get('/admin/expense-categories'),
        api.get('/admin/vendors'),
        api.get('/admin/expenses', { params: { year: selectedYear, month: selectedMonth } }),
        api.get('/admin/recurring-expenses'),
        api.get('/admin/expenses/summary', { params: { year: selectedYear } })
      ]);
      setCategories(catRes.data);
      setVendors(vendorRes.data);
      setExpenses(expRes.data);
      setRecurringExpenses(recRes.data);
      setSummary(sumRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter expenses
  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = !searchTerm || 
      exp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || exp.category_id === filterCategory;
    const matchesVendor = !filterVendor || exp.vendor_id === filterVendor;
    return matchesSearch && matchesCategory && matchesVendor;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Category CRUD
  const handleSaveCategory = async (formData) => {
    try {
      if (editingItem) {
        await api.put(`/admin/expense-categories/${editingItem.category_id}`, formData);
        toast.success('Category updated');
      } else {
        await api.post('/admin/expense-categories', formData);
        toast.success('Category created');
      }
      setShowCategoryModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/admin/expense-categories/${categoryId}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Vendor CRUD
  const handleSaveVendor = async (formData) => {
    try {
      if (editingItem) {
        await api.put(`/admin/vendors/${editingItem.vendor_id}`, formData);
        toast.success('Vendor updated');
      } else {
        await api.post('/admin/vendors', formData);
        toast.success('Vendor created');
      }
      setShowVendorModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await api.delete(`/admin/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  // Expense CRUD
  const handleSaveExpense = async (formData) => {
    try {
      if (editingItem) {
        await api.put(`/admin/expenses/${editingItem.expense_id}`, formData);
        toast.success('Expense updated');
      } else {
        await api.post('/admin/expenses', formData);
        toast.success('Expense recorded');
      }
      setShowExpenseModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    try {
      await api.delete(`/admin/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  // Recurring Expense CRUD
  const handleSaveRecurring = async (formData) => {
    try {
      if (editingItem) {
        await api.put(`/admin/recurring-expenses/${editingItem.recurring_id}`, formData);
        toast.success('Recurring expense updated');
      } else {
        await api.post('/admin/recurring-expenses', formData);
        toast.success('Recurring expense created');
      }
      setShowRecurringModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recurring expense');
    }
  };

  const handleDeleteRecurring = async (recurringId) => {
    if (!confirm('Are you sure you want to delete this recurring expense?')) return;
    try {
      await api.delete(`/admin/recurring-expenses/${recurringId}`);
      toast.success('Recurring expense deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete recurring expense');
    }
  };

  const handleGenerateFromRecurring = async (data) => {
    try {
      await api.post(`/admin/recurring-expenses/${generatingRecurring.recurring_id}/generate`, {
        expense_month: data.month,
        expense_year: data.year,
        custom_amount: data.amount
      });
      toast.success('Expense generated from recurring template');
      setShowGenerateModal(false);
      setGeneratingRecurring(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate expense');
    }
  };

  // Payment handler
  const handleRecordPayment = async (formData) => {
    try {
      await api.post(`/admin/expenses/${payingExpense.expense_id}/pay`, formData);
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setPayingExpense(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  // Export functions
  const exportToCSV = () => {
    const headers = ['Month', 'Date', 'Description', 'Category', 'Vendor', 'Amount', 'Paid', 'Status', 'Payment Date', 'Payment Method', 'Reference'];
    const rows = filteredExpenses.map(exp => [
      `${MONTHS.find(m => m.value === exp.expense_month)?.label || ''} ${exp.expense_year || ''}`,
      exp.expense_date,
      exp.description,
      exp.category_name,
      exp.vendor_name || '-',
      exp.amount,
      exp.paid_amount || 0,
      exp.status || 'pending',
      exp.payment_date || '-',
      exp.payment_method,
      exp.payment_reference || '-'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${selectedYear}${selectedMonth ? '_' + selectedMonth : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      partial: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || styles.pending}>{status || 'pending'}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="expenditure-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Expenditure</h1>
          <p className="text-sm text-slate-500">Track and manage community expenses</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <IndianRupee className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Expenses</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(summary?.total_expenses)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Transactions</p>
                    <p className="text-2xl font-bold text-slate-800">{summary?.expense_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Vendors</p>
                    <p className="text-2xl font-bold text-slate-800">{vendors.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <RefreshCw className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Recurring</p>
                    <p className="text-2xl font-bold text-slate-800">{recurringExpenses.filter(r => r.is_active).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Category Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.category_breakdown?.length > 0 ? (
                <div className="space-y-3">
                  {summary.category_breakdown.map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">{cat.name}</span>
                          <span className="text-sm text-slate-600">{formatCurrency(cat.total)}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${(cat.total / summary.total_expenses) * 100}%`,
                              backgroundColor: cat.color
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 w-12 text-right">
                        {Math.round((cat.total / summary.total_expenses) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No expenses recorded for {selectedYear}</p>
              )}
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.monthly_breakdown?.length > 0 ? (
                <div className="space-y-2">
                  {summary.monthly_breakdown.map((month, idx) => {
                    const maxAmount = Math.max(...summary.monthly_breakdown.map(m => m.total));
                    return (
                      <div key={idx} className="flex items-center gap-4">
                        <span className="text-sm text-slate-600 w-20">{month.month}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded transition-all"
                            style={{ width: `${(month.total / maxAmount) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium w-24 text-right">{formatCurrency(month.total)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No monthly data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-[200px]"
                  data-testid="expense-search"
                />
              </div>
              <Select value={selectedMonth?.toString() || "all"} onValueChange={(v) => setSelectedMonth(v === "all" ? null : parseInt(v))}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterVendor || "all"} onValueChange={(v) => setFilterVendor(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowReportModal(true)} data-testid="generate-report-btn">
                <Printer className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
              <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button onClick={() => { setEditingItem(null); setShowExpenseModal(true); }} data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Month</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Expense Date</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Description</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Category</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Vendor</th>
                      <th className="text-right p-4 text-sm font-medium text-slate-600">Amount</th>
                      <th className="text-right p-4 text-sm font-medium text-slate-600">Paid</th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-slate-500">
                          No expenses found
                        </td>
                      </tr>
                    ) : (
                      filteredExpenses.map(exp => (
                        <tr key={exp.expense_id} className="border-b hover:bg-slate-50">
                          <td className="p-4 text-sm">
                            <span className="font-medium">{MONTHS.find(m => m.value === exp.expense_month)?.label?.slice(0,3) || '-'}</span>
                            <span className="text-slate-500 ml-1">{exp.expense_year}</span>
                          </td>
                          <td className="p-4 text-sm">{formatDate(exp.expense_date)}</td>
                          <td className="p-4">
                            <p className="text-sm font-medium">{exp.description}</p>
                            {exp.payment_date && (
                              <p className="text-xs text-slate-500">Paid: {formatDate(exp.payment_date)}</p>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" style={{ borderColor: exp.category_color, color: exp.category_color }}>
                              {exp.category_name}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{exp.vendor_name || '-'}</td>
                          <td className="p-4 text-sm font-medium text-right">{formatCurrency(exp.amount)}</td>
                          <td className="p-4 text-sm text-right">{formatCurrency(exp.paid_amount || 0)}</td>
                          <td className="p-4 text-center">{getStatusBadge(exp.status)}</td>
                          <td className="p-4">
                            <div className="flex justify-center gap-1">
                              {exp.status !== 'paid' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                  onClick={() => { setPayingExpense(exp); setShowPaymentModal(true); }}
                                  title="Record Payment"
                                >
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                              )}
                              {exp.receipt_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => loadReceipt(exp.receipt_url)}
                                  title="View Receipt"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditingItem(exp); setShowExpenseModal(true); }}
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteExpense(exp.expense_id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{vendors.length} vendors registered</p>
            <Button onClick={() => { setEditingItem(null); setShowVendorModal(true); }} data-testid="add-vendor-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(vendor => (
              <Card key={vendor.vendor_id}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-800">{vendor.name}</h3>
                      {vendor.contact_person && (
                        <p className="text-sm text-slate-500">{vendor.contact_person}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingItem(vendor); setShowVendorModal(true); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => handleDeleteVendor(vendor.vendor_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    {vendor.phone && <p className="text-slate-600">📞 {vendor.phone}</p>}
                    {vendor.email && <p className="text-slate-600">✉️ {vendor.email}</p>}
                    {vendor.gst_number && <p className="text-slate-600">GST: {vendor.gst_number}</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
            {vendors.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                No vendors added yet. Click "Add Vendor" to get started.
              </div>
            )}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{categories.length} expense categories</p>
            <Button onClick={() => { setEditingItem(null); setShowCategoryModal(true); }} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(cat => (
              <Card key={cat.category_id}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }} />
                    <h3 className="font-semibold text-slate-800 flex-1">{cat.name}</h3>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingItem(cat); setShowCategoryModal(true); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => handleDeleteCategory(cat.category_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {cat.description && <p className="text-sm text-slate-500">{cat.description}</p>}
                </CardContent>
              </Card>
            ))}
            {categories.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                No categories added yet. Click "Add Category" to get started.
              </div>
            )}
          </div>
        </TabsContent>

        {/* Recurring Tab */}
        <TabsContent value="recurring" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{recurringExpenses.length} recurring expenses</p>
            <Button onClick={() => { setEditingItem(null); setShowRecurringModal(true); }} data-testid="add-recurring-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Recurring
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Description</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Category</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Vendor</th>
                      <th className="text-right p-4 text-sm font-medium text-slate-600">Amount</th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Frequency</th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recurringExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          No recurring expenses set up
                        </td>
                      </tr>
                    ) : (
                      recurringExpenses.map(rec => (
                        <tr key={rec.recurring_id} className="border-b hover:bg-slate-50">
                          <td className="p-4">
                            <p className="text-sm font-medium">{rec.description}</p>
                            {rec.last_generated && (
                              <p className="text-xs text-slate-500">Last: {formatDate(rec.last_generated)}</p>
                            )}
                          </td>
                          <td className="p-4 text-sm">{rec.category_name}</td>
                          <td className="p-4 text-sm text-slate-600">{rec.vendor_name || '-'}</td>
                          <td className="p-4 text-sm font-medium text-right">{formatCurrency(rec.amount)}</td>
                          <td className="p-4 text-center">
                            <Badge variant="outline">{rec.frequency}</Badge>
                          </td>
                          <td className="p-4 text-center">
                            <Badge variant={rec.is_active ? 'default' : 'secondary'}>
                              {rec.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Generate expense for a month"
                                onClick={() => { setGeneratingRecurring(rec); setShowGenerateModal(true); }}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditingItem(rec); setShowRecurringModal(true); }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                onClick={() => handleDeleteRecurring(rec.recurring_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Modal */}
      <CategoryModal
        open={showCategoryModal}
        onClose={() => { setShowCategoryModal(false); setEditingItem(null); }}
        onSave={handleSaveCategory}
        initialData={editingItem}
      />

      {/* Vendor Modal */}
      <VendorModal
        open={showVendorModal}
        onClose={() => { setShowVendorModal(false); setEditingItem(null); }}
        onSave={handleSaveVendor}
        initialData={editingItem}
      />

      {/* Expense Modal */}
      <ExpenseModal
        open={showExpenseModal}
        onClose={() => { setShowExpenseModal(false); setEditingItem(null); }}
        onSave={handleSaveExpense}
        initialData={editingItem}
        categories={categories}
        vendors={vendors}
      />

      {/* Recurring Modal */}
      <RecurringModal
        open={showRecurringModal}
        onClose={() => { setShowRecurringModal(false); setEditingItem(null); }}
        onSave={handleSaveRecurring}
        initialData={editingItem}
        categories={categories}
        vendors={vendors}
      />

      {/* Receipt Viewer Modal */}
      <Dialog open={showReceiptModal} onOpenChange={(open) => !open && closeReceiptModal()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[400px] bg-slate-100 rounded-lg">
            {receiptLoading ? (
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                <span className="text-sm text-slate-500">Loading receipt...</span>
              </div>
            ) : receiptBlobUrl ? (
              receiptUrl.endsWith('.pdf') ? (
                <iframe src={receiptBlobUrl} className="w-full h-[500px]" title="Receipt PDF" />
              ) : (
                <img src={receiptBlobUrl} alt="Receipt" className="max-h-[500px] object-contain" />
              )
            ) : (
              <div className="text-slate-400">No receipt to display</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <PaymentModal
        open={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPayingExpense(null); }}
        onSave={handleRecordPayment}
        expense={payingExpense}
      />

      {/* Generate from Recurring Modal */}
      <GenerateModal
        open={showGenerateModal}
        onClose={() => { setShowGenerateModal(false); setGeneratingRecurring(null); }}
        onGenerate={handleGenerateFromRecurring}
        recurring={generatingRecurring}
      />

      {/* Report Modal */}
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        selectedYear={selectedYear}
      />
    </div>
  );
}

// Category Modal Component
function CategoryModal({ open, onClose, onSave, initialData }) {
  const [form, setForm] = useState({ name: '', description: '', color: '#6366f1' });

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || '#6366f1'
      });
    } else {
      setForm({ name: '', description: '', color: '#6366f1' });
    }
  }, [initialData, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Category name is required');
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Category' : 'Add Category'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Maintenance, Utilities"
              data-testid="category-name-input"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
            />
          </div>
          <div>
            <Label>Color</Label>
            <div className="flex gap-2 mt-2">
              {DEFAULT_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 ${form.color === color ? 'border-slate-800' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setForm({ ...form, color })}
                />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-category-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Vendor Modal Component
function VendorModal({ open, onClose, onSave, initialData }) {
  const [form, setForm] = useState({
    name: '', contact_person: '', email: '', phone: '', address: '',
    gst_number: '', pan_number: '', bank_name: '', account_number: '', ifsc_code: '', notes: ''
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || '',
        contact_person: initialData.contact_person || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        address: initialData.address || '',
        gst_number: initialData.gst_number || '',
        pan_number: initialData.pan_number || '',
        bank_name: initialData.bank_name || '',
        account_number: initialData.account_number || '',
        ifsc_code: initialData.ifsc_code || '',
        notes: initialData.notes || ''
      });
    } else {
      setForm({
        name: '', contact_person: '', email: '', phone: '', address: '',
        gst_number: '', pan_number: '', bank_name: '', account_number: '', ifsc_code: '', notes: ''
      });
    }
  }, [initialData, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Vendor name is required');
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Vendor Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Company/Person name"
                data-testid="vendor-name-input"
              />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input
                value={form.contact_person}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                placeholder="Contact person name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Full address"
              />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input
                value={form.gst_number}
                onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
                placeholder="GST number"
              />
            </div>
            <div>
              <Label>PAN Number</Label>
              <Input
                value={form.pan_number}
                onChange={(e) => setForm({ ...form, pan_number: e.target.value })}
                placeholder="PAN number"
              />
            </div>
          </div>
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Bank Details</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="Bank name"
                />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  placeholder="Account number"
                />
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input
                  value={form.ifsc_code}
                  onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })}
                  placeholder="IFSC code"
                />
              </div>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Additional notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-vendor-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Expense Modal Component
function ExpenseModal({ open, onClose, onSave, initialData, categories, vendors }) {
  const currentDate = new Date();
  const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  
  const [form, setForm] = useState({
    category_id: '', vendor_id: '', amount: '', description: '',
    expense_period: currentMonthYear,
    expense_date: '', payment_date: '',
    payment_method: 'cash', payment_reference: '', notes: '', receipt_url: ''
  });
  const [uploading, setUploading] = useState(false);

  // Generate month-year options for the last 2 years and next year
  const getMonthYearOptions = () => {
    const options = [];
    const startYear = currentDate.getFullYear() - 1;
    const endYear = currentDate.getFullYear() + 1;
    
    for (let year = endYear; year >= startYear; year--) {
      for (let month = 12; month >= 1; month--) {
        const value = `${year}-${String(month).padStart(2, '0')}`;
        const label = `${MONTHS.find(m => m.value === month)?.label} ${year}`;
        options.push({ value, label });
      }
    }
    return options;
  };

  const monthYearOptions = getMonthYearOptions();

  useEffect(() => {
    if (initialData) {
      const period = initialData.expense_month && initialData.expense_year 
        ? `${initialData.expense_year}-${String(initialData.expense_month).padStart(2, '0')}`
        : currentMonthYear;
      setForm({
        category_id: initialData.category_id || '',
        vendor_id: initialData.vendor_id || '',
        amount: initialData.amount?.toString() || '',
        description: initialData.description || '',
        expense_period: period,
        expense_date: initialData.expense_date || '',
        payment_date: initialData.payment_date || '',
        payment_method: initialData.payment_method || 'cash',
        payment_reference: initialData.payment_reference || '',
        notes: initialData.notes || '',
        receipt_url: initialData.receipt_url || ''
      });
    } else {
      setForm({
        category_id: '', vendor_id: '', amount: '', description: '',
        expense_period: currentMonthYear,
        expense_date: '', payment_date: '',
        payment_method: 'cash', payment_reference: '', notes: '', receipt_url: ''
      });
    }
  }, [initialData, open]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/expenses/upload-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm({ ...form, receipt_url: res.data.receipt_url });
      toast.success('Receipt uploaded');
    } catch (error) {
      toast.error('Failed to upload receipt');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error('Category is required');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Valid amount is required');
    if (!form.description.trim()) return toast.error('Description is required');
    if (!form.expense_period) return toast.error('Expense period is required');
    
    // Parse month and year from expense_period
    const [year, month] = form.expense_period.split('-').map(Number);
    
    onSave({
      ...form,
      expense_month: month,
      expense_year: year,
      amount: parseFloat(form.amount),
      vendor_id: form.vendor_id || null,
      expense_date: form.expense_date || null,
      payment_date: form.payment_date || null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "none" ? "" : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No vendor</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Expense Period *</Label>
              <Select value={form.expense_period} onValueChange={(v) => setForm({ ...form, expense_period: v })}>
                <SelectTrigger data-testid="expense-period-select">
                  <SelectValue placeholder="Select month & year" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {monthYearOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expense Date (Optional)</Label>
              <Input
                type="date"
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                data-testid="expense-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
                data-testid="expense-amount-input"
              />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                data-testid="expense-payment-date-input"
              />
            </div>
            <div className="col-span-2">
              <Label>Description *</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What was this expense for?"
                data-testid="expense-description-input"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference/Transaction ID</Label>
              <Input
                value={form.payment_reference}
                onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                placeholder="Cheque no, UPI ref, etc."
              />
            </div>
          </div>
          <div>
            <Label>Receipt</Label>
            <div className="flex items-center gap-4 mt-2">
              <Input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                disabled={uploading}
                className="flex-1"
              />
              {form.receipt_url && (
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, receipt_url: '' })}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {form.receipt_url && <p className="text-xs text-green-600 mt-1">Receipt attached ✓</p>}
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Additional notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={uploading} data-testid="save-expense-btn">
              {uploading ? 'Uploading...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Recurring Expense Modal Component
function RecurringModal({ open, onClose, onSave, initialData, categories, vendors }) {
  const [form, setForm] = useState({
    category_id: '', vendor_id: '', amount: '', description: '',
    frequency: 'monthly', start_date: '', end_date: '', payment_method: 'cash', notes: '', is_active: true
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        category_id: initialData.category_id || '',
        vendor_id: initialData.vendor_id || '',
        amount: initialData.amount?.toString() || '',
        description: initialData.description || '',
        frequency: initialData.frequency || 'monthly',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
        payment_method: initialData.payment_method || 'cash',
        notes: initialData.notes || '',
        is_active: initialData.is_active !== false
      });
    } else {
      setForm({
        category_id: '', vendor_id: '', amount: '', description: '',
        frequency: 'monthly', start_date: new Date().toISOString().split('T')[0],
        end_date: '', payment_method: 'cash', notes: '', is_active: true
      });
    }
  }, [initialData, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error('Category is required');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Valid amount is required');
    if (!form.description.trim()) return toast.error('Description is required');
    if (!form.start_date) return toast.error('Start date is required');
    
    onSave({
      ...form,
      amount: parseFloat(form.amount),
      vendor_id: form.vendor_id || null,
      end_date: form.end_date || null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Recurring Expense' : 'Add Recurring Expense'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "none" ? "" : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No vendor</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Frequency *</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description *</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g., Monthly security contract"
              />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label>End Date (optional)</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Additional notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-recurring-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Payment Modal Component
function PaymentModal({ open, onClose, onSave, expense }) {
  const [form, setForm] = useState({
    amount: '', payment_method: 'cash', payment_reference: '', payment_date: '', notes: ''
  });

  useEffect(() => {
    if (expense && open) {
      const pendingAmount = (expense.amount || 0) - (expense.paid_amount || 0);
      setForm({
        amount: pendingAmount.toString(),
        payment_method: 'cash',
        payment_reference: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
    }
  }, [expense, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Valid amount is required');
      return;
    }
    onSave({
      ...form,
      amount: parseFloat(form.amount)
    });
  };

  if (!expense) return null;

  const pendingAmount = (expense.amount || 0) - (expense.paid_amount || 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>
        <div className="bg-slate-50 p-4 rounded-lg mb-4">
          <p className="font-medium">{expense.description}</p>
          <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
            <div>
              <span className="text-slate-500">Total:</span>
              <span className="ml-2 font-medium">₹{expense.amount?.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-slate-500">Paid:</span>
              <span className="ml-2 text-green-600">₹{(expense.paid_amount || 0).toLocaleString()}</span>
            </div>
            <div>
              <span className="text-slate-500">Pending:</span>
              <span className="ml-2 text-red-600">₹{pendingAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                max={pendingAmount}
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference</Label>
              <Input
                value={form.payment_reference}
                onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                placeholder="Cheque no, UPI ref"
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Payment notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="record-payment-btn">Record Payment</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Report Modal Component
function ReportModal({ open, onClose, selectedYear }) {
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [year, setYear] = useState(selectedYear.toString());
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const generateReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/expenses/monthly-report', {
        params: { month: parseInt(month), year: parseInt(year) }
      });
      setReportData(res.data);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (!reportData) return;
    
    // Create a branded printable HTML and trigger print
    const printWindow = window.open('', '_blank');
    const monthName = MONTHS.find(m => m.value === parseInt(month))?.label || month;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Expense Report - ${monthName} ${year}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
            .header { background: linear-gradient(135deg, #059669, #10b981); color: white; padding: 20px; margin: -20px -20px 20px -20px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header .subtitle { opacity: 0.9; font-size: 12px; margin-top: 4px; }
            .brand-tagline { font-size: 10px; opacity: 0.8; }
            h2 { color: #1e293b; margin-top: 20px; font-size: 18px; }
            h3 { color: #334155; margin-top: 20px; font-size: 14px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
            .report-title { text-align: center; margin: 20px 0; }
            .report-title h2 { color: #059669; margin: 0; }
            .report-title p { color: #64748b; margin: 5px 0 0; }
            .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; }
            .summary-card { background: #f8fafc; padding: 15px; border-radius: 8px; border: 1px solid #e2e8f0; }
            .summary-card h3 { margin: 0; font-size: 12px; color: #64748b; border: none; padding: 0; }
            .summary-card p { margin: 5px 0 0; font-size: 20px; font-weight: bold; color: #1e293b; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; }
            th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
            th { background: #f1f5f9; font-weight: 600; color: #334155; }
            .text-right { text-align: right; }
            .status-paid { color: #16a34a; font-weight: 500; }
            .status-partial { color: #ca8a04; font-weight: 500; }
            .status-pending { color: #dc2626; font-weight: 500; }
            .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e2e8f0; }
            .footer p { color: #64748b; font-size: 10px; margin: 0; }
            @media print {
              body { padding: 0; }
              .header { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>☕ FreeCoffee</h1>
            <div class="subtitle">Your Free CommunityOS</div>
          </div>
          
          <div class="report-title">
            <h2>Monthly Expense Report</h2>
            <p>${reportData.community_name} | ${monthName} ${year}</p>
          </div>
          
          <div class="summary">
            <div class="summary-card">
              <h3>Total Expenses</h3>
              <p>₹${reportData.summary.total_amount.toLocaleString()}</p>
            </div>
            <div class="summary-card">
              <h3>Total Paid</h3>
              <p style="color: #16a34a;">₹${reportData.summary.total_paid.toLocaleString()}</p>
            </div>
            <div class="summary-card">
              <h3>Total Pending</h3>
              <p style="color: #dc2626;">₹${reportData.summary.total_pending.toLocaleString()}</p>
            </div>
            <div class="summary-card">
              <h3>Transactions</h3>
              <p>${reportData.summary.expense_count}</p>
            </div>
          </div>
          
          <h3>Category Breakdown</h3>
          <table>
            <tr>
              <th>Category</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Paid</th>
              <th class="text-right">Count</th>
            </tr>
            ${reportData.category_breakdown.map(cat => `
              <tr>
                <td>${cat.name}</td>
                <td class="text-right">₹${cat.total.toLocaleString()}</td>
                <td class="text-right">₹${cat.paid.toLocaleString()}</td>
                <td class="text-right">${cat.count}</td>
              </tr>
            `).join('')}
          </table>
          
          <h3>Expense Details</h3>
          <table>
            <tr>
              <th>Description</th>
              <th>Category</th>
              <th>Vendor</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Paid</th>
              <th>Status</th>
            </tr>
            ${reportData.expenses.map(exp => `
              <tr>
                <td>${exp.description}</td>
                <td>${exp.category_name}</td>
                <td>${exp.vendor_name || '-'}</td>
                <td class="text-right">₹${exp.amount.toLocaleString()}</td>
                <td class="text-right">₹${(exp.paid_amount || 0).toLocaleString()}</td>
                <td class="status-${exp.status || 'pending'}">${exp.status || 'pending'}</td>
              </tr>
            `).join('')}
          </table>
          
          <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} at ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
            <p>Powered by FreeCoffee - Your Free CommunityOS</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Monthly Report</DialogTitle>
        </DialogHeader>
        
        <div className="flex gap-4 items-end mb-4">
          <div className="flex-1">
            <Label>Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label>Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generateReport} disabled={loading} data-testid="generate-report-submit">
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </div>

        {reportData && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg">
                {reportData.community_name} - {reportData.month_name} {reportData.year}
              </h3>
              <Button variant="outline" onClick={downloadPDF}>
                <Download className="h-4 w-4 mr-2" />
                Print/Download PDF
              </Button>
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-slate-500">Total Expenses</p>
                  <p className="text-2xl font-bold">₹{reportData.summary.total_amount.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-slate-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">₹{reportData.summary.total_paid.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-red-600">₹{reportData.summary.total_pending.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-slate-500">Transactions</p>
                  <p className="text-2xl font-bold">{reportData.summary.expense_count}</p>
                </CardContent>
              </Card>
            </div>

            {reportData.category_breakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Category Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reportData.category_breakdown.map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span>{cat.name}</span>
                          <Badge variant="outline" className="text-xs">{cat.count}</Badge>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">₹{cat.total.toLocaleString()}</span>
                          <span className="text-sm text-slate-500 ml-2">(Paid: ₹{cat.paid.toLocaleString()})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {reportData.expenses.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Expense Details</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm">Date</th>
                        <th className="text-left p-3 text-sm">Description</th>
                        <th className="text-left p-3 text-sm">Category</th>
                        <th className="text-right p-3 text-sm">Amount</th>
                        <th className="text-right p-3 text-sm">Paid</th>
                        <th className="text-center p-3 text-sm">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.expenses.map((exp, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-3 text-sm">{exp.expense_date}</td>
                          <td className="p-3 text-sm">{exp.description}</td>
                          <td className="p-3 text-sm">{exp.category_name}</td>
                          <td className="p-3 text-sm text-right">₹{exp.amount.toLocaleString()}</td>
                          <td className="p-3 text-sm text-right">₹{(exp.paid_amount || 0).toLocaleString()}</td>
                          <td className="p-3 text-center">
                            <Badge className={
                              exp.status === 'paid' ? 'bg-green-100 text-green-700' :
                              exp.status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {exp.status || 'pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Generate from Recurring Modal Component
function GenerateModal({ open, onClose, onGenerate, recurring }) {
  const currentDate = new Date();
  const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const [selectedPeriod, setSelectedPeriod] = useState(currentMonthYear);
  const [customAmount, setCustomAmount] = useState('');

  useEffect(() => {
    if (open && recurring) {
      setSelectedPeriod(currentMonthYear);
      setCustomAmount(recurring.amount?.toString() || '');
    }
  }, [open, recurring]);

  // Generate month-year options
  const getMonthYearOptions = () => {
    const options = [];
    const startYear = currentDate.getFullYear() - 1;
    const endYear = currentDate.getFullYear() + 1;
    
    for (let year = endYear; year >= startYear; year--) {
      for (let month = 12; month >= 1; month--) {
        const value = `${year}-${String(month).padStart(2, '0')}`;
        const label = `${MONTHS.find(m => m.value === month)?.label} ${year}`;
        options.push({ value, label });
      }
    }
    return options;
  };

  const monthYearOptions = getMonthYearOptions();

  const handleGenerate = () => {
    if (!customAmount || parseFloat(customAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    const [year, month] = selectedPeriod.split('-').map(Number);
    onGenerate({ month, year, amount: parseFloat(customAmount) });
  };

  if (!recurring) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Expense</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="font-medium text-slate-800">{recurring.description}</p>
            <div className="flex gap-4 mt-2 text-sm text-slate-600">
              <span>Default Amount: ₹{recurring.amount?.toLocaleString()}</span>
              <span>Frequency: {recurring.frequency}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Generate for Month *</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger data-testid="generate-period-select">
                  <SelectValue placeholder="Select month & year" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {monthYearOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount for this month (₹) *</Label>
              <Input
                type="number"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Enter amount"
                data-testid="generate-amount-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleGenerate} data-testid="generate-expense-btn">
              Generate Expense
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
